import { useState } from 'react';
import { ConsentLink, type RunAction } from '../AddClient.tsx';
import { api, type Tenant } from '../api.ts';
import { message } from '../format.ts';

/**
 * The per-row client-management drawers.
 *
 * They were split out of the old `ClientAdmin` screen when it crossed the
 * 800-line ceiling, and stayed put when that screen merged into `Portfolio`:
 * everything that opens *inside* a row — the action drawers and the confirm bar
 * they share — changes for its own reasons, and both of that screen's tables
 * open into them. Behaviour is identical to before either move.
 *
 * The add-client form left again when the Onboarding screen needed it too: it
 * lives in `AddClient.tsx`, along with the consent-link reveal the reconnect
 * drawer below shares with it. That screen has since become two tabs on this
 * one, so both call sites are now the Clients screen — the form stays where it
 * is, because a component named after what it does outlives the screens that
 * happen to render it.
 */

/** Which per-row drawer is open. Shared by both tables on the Clients screen. */
export type RowKind = 'edit' | 'reconnect' | 'archive' | 'unarchive' | 'purge';

interface RowDrawerProps {
  readonly kind: RowKind;
  readonly tenant: Tenant;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onRun: RunAction;
}

export const RowDrawer = ({ kind, tenant, busy, onCancel, onRun }: RowDrawerProps) => {
  if (kind === 'edit') return <EditDrawer tenant={tenant} busy={busy} onCancel={onCancel} onRun={onRun} />;
  if (kind === 'reconnect') return <ReconnectDrawer tenant={tenant} onCancel={onCancel} />;
  if (kind === 'purge') return <PurgeDrawer tenant={tenant} busy={busy} onCancel={onCancel} onRun={onRun} />;

  const archive = kind === 'archive';
  return (
    <div className="admin-drawer">
      <p className="admin-drawer-text">
        {archive ? (
          <>
            Archive <strong>{tenant.displayName}</strong>. They leave every live list — the estate, the
            sweep, the consent chase — and nothing they have recorded is removed. You can unarchive them
            at any time, and only then can they be purged.
          </>
        ) : (
          <>
            Unarchive <strong>{tenant.displayName}</strong> and return them to every live list. Their
            evidence is exactly as they left it.
          </>
        )}
      </p>
      <ConfirmBar
        label={archive ? `Archive ${tenant.displayName}` : `Unarchive ${tenant.displayName}`}
        busy={busy}
        disabled={false}
        onConfirm={() =>
          void onRun(
            () => (archive ? api.archiveTenant(tenant.id) : api.unarchiveTenant(tenant.id)).then(() => undefined),
            archive ? `${tenant.displayName} has been archived.` : `${tenant.displayName} has been unarchived.`,
          )
        }
        onCancel={onCancel}
      />
    </div>
  );
};

interface EditDrawerProps {
  readonly tenant: Tenant;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onRun: RunAction;
}

const EditDrawer = ({ tenant, busy, onCancel, onRun }: EditDrawerProps) => {
  const [displayName, setDisplayName] = useState(tenant.displayName);
  const [contactName, setContactName] = useState(tenant.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(tenant.contactEmail ?? '');

  const trimmed = (value: string): string | null => (value.trim() === '' ? null : value.trim());
  const nameChanged = displayName.trim() !== tenant.displayName;
  const contactNameChanged = trimmed(contactName) !== tenant.contactName;
  const contactEmailChanged = trimmed(contactEmail) !== tenant.contactEmail;
  const changed = nameChanged || contactNameChanged || contactEmailChanged;

  const submit = () => {
    // Only the fields that changed are sent: an omitted field is left unchanged
    // and an explicit null clears a contact, and the server distinguishes the
    // two. Sending only what moved keeps that distinction honest.
    void onRun(
      () =>
        api
          .editTenant(tenant.id, {
            ...(nameChanged ? { displayName: displayName.trim() } : {}),
            ...(contactNameChanged ? { contactName: trimmed(contactName) } : {}),
            ...(contactEmailChanged ? { contactEmail: trimmed(contactEmail) } : {}),
          })
          .then(() => undefined),
      `${displayName.trim()}’s details have been updated.`,
    );
  };

  return (
    <div className="admin-drawer">
      <p className="admin-drawer-text">
        Correct <strong>{tenant.displayName}</strong>’s name and onboarding contact. The Microsoft
        tenant id is not editable — it is the client’s identity, not a detail.
      </p>
      <div className="field admin-drawer-field">
        <label htmlFor={`edit-name-${tenant.id}`}>Client name</label>
        <input
          id={`edit-name-${tenant.id}`}
          type="text"
          value={displayName}
          disabled={busy}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>
      <div className="field admin-drawer-field">
        <label htmlFor={`edit-contact-${tenant.id}`}>Contact name</label>
        <input
          id={`edit-contact-${tenant.id}`}
          type="text"
          value={contactName}
          disabled={busy}
          onChange={(event) => setContactName(event.target.value)}
        />
      </div>
      <div className="field admin-drawer-field">
        <label htmlFor={`edit-email-${tenant.id}`}>Contact email</label>
        <input
          id={`edit-email-${tenant.id}`}
          type="email"
          autoCapitalize="off"
          spellCheck={false}
          value={contactEmail}
          disabled={busy}
          onChange={(event) => setContactEmail(event.target.value)}
        />
        <span className="hint">Clear a field to remove that contact detail.</span>
      </div>
      <ConfirmBar
        label="Save changes"
        busy={busy}
        disabled={!changed || displayName.trim() === ''}
        onConfirm={submit}
        onCancel={onCancel}
      />
    </div>
  );
};

interface ReconnectDrawerProps {
  readonly tenant: Tenant;
  readonly onCancel: () => void;
}

const ReconnectDrawer = ({ tenant, onCancel }: ReconnectDrawerProps) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchUrl = async () => {
    setBusy(true);
    setError(null);
    try {
      const { consentUrl } = await api.consentUrl(tenant.id);
      setUrl(consentUrl);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-drawer">
      <p className="admin-drawer-text">
        Get a fresh consent link for <strong>{tenant.displayName}</strong>. Send it to their Global
        Administrator when a connection has broken — a revoked consent, or permissions that were
        granted without everything DriftWatch reads with.
      </p>
      {error !== null && <div className="error admin-drawer-error">{error}</div>}
      {url === null ? (
        <div className="toolbar admin-drawer-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            aria-busy={busy || undefined}
            disabled={busy}
            onClick={() => void fetchUrl()}
          >
            {busy ? 'Fetching…' : 'Get consent link'}
          </button>
          <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      ) : (
        <ConsentLink
          url={url}
          what={`Consent link for ${tenant.displayName}`}
          note="DriftWatch never emails your clients. Copy this and send it yourself."
        />
      )}
    </div>
  );
};

interface PurgeDrawerProps {
  readonly tenant: Tenant;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onRun: RunAction;
}

/**
 * The one irreversible control in the console, so the confirmation is a typed
 * match rather than a second click: the operator has to type the client's name,
 * which cannot happen by momentum. The button stays inert until the typed string
 * is exact.
 */
const PurgeDrawer = ({ tenant, busy, onCancel, onRun }: PurgeDrawerProps) => {
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === tenant.displayName;

  return (
    <div className="admin-drawer admin-drawer-purge">
      <p className="admin-drawer-text">
        Purge <strong>{tenant.displayName}</strong> for good. This destroys every scan, finding and
        report they own, and the sealed credential DriftWatch reads them with. It cannot be undone,
        and there is no backup to restore from. Archiving them was the reversible step; this is not.
      </p>
      <div className="field admin-drawer-field">
        <label htmlFor={`purge-${tenant.id}`}>
          Type <span className="admin-purge-name">{tenant.displayName}</span> to confirm
        </label>
        <input
          id={`purge-${tenant.id}`}
          type="text"
          autoComplete="off"
          value={typed}
          disabled={busy}
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>
      <ConfirmBar
        label={`Purge ${tenant.displayName} permanently`}
        busy={busy}
        disabled={!matches}
        onConfirm={() =>
          void onRun(
            () => api.purgeTenant(tenant.id).then(() => undefined),
            `${tenant.displayName} has been purged. Nothing of theirs remains.`,
          )
        }
        onCancel={onCancel}
      />
    </div>
  );
};

interface ConfirmBarProps {
  readonly label: string;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const ConfirmBar = ({ label, busy, disabled, onConfirm, onCancel }: ConfirmBarProps) => (
  <div className="toolbar admin-drawer-actions">
    <button
      type="button"
      className="btn btn-primary btn-sm"
      aria-busy={busy || undefined}
      disabled={busy || disabled}
      onClick={onConfirm}
    >
      {label}
    </button>
    <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={onCancel}>
      Cancel
    </button>
  </div>
);
